import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Grid, Card, CardContent, Typography, Chip, LinearProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Button, CircularProgress, Alert, alpha, useTheme, Paper, Divider,
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import { formatDateLong } from '../../utils/dateUtils';
import LeaveSection from './LeaveSection';
import StaffScheduleSection from './StaffScheduleSection';
import MonthYearPicker, { formatPeriodLabel, getDefaultPeriod } from '../../components/common/MonthYearPicker';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount || 0);
}

function StatusChip({ status }) {
  const norm = (() => {
    const s = (status || '').toLowerCase();
    if (['completed', 'invoiced'].includes(s)) return 'Completed';
    if (['approved', 'in progress', 'in design', 'proofing', 'uploaded'].includes(s)) return 'In Progress';
    if (['cancelled', 'canceled'].includes(s)) return 'Cancelled';
    return 'Pending';
  })();
  const map = {
    Completed:     { color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    'In Progress': { color: 'info',    icon: <AccessTimeIcon fontSize="small" /> },
    Pending:       { color: 'warning', icon: <WarningIcon fontSize="small" /> },
    Cancelled:     { color: 'error',   icon: <DoNotDisturbIcon fontSize="small" /> },
  };
  const { color, icon } = map[norm] || { color: 'default', icon: null };
  return <Chip size="small" label={norm} color={color} icon={icon} variant="outlined" />;
}

// ─── MetricCard ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, change, trend, icon, color }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%', borderRadius: 2,
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.18)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 40px 0 rgba(0,0,0,0.10)' },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 48, height: 48, borderRadius: 1.5, flexShrink: 0, mr: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(color || theme.palette.primary.main, 0.1),
              color: color || theme.palette.primary.main,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {value}
            </Typography>
          </Box>
        </Box>
        {change !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: trend === 'up' ? 'success.main' : 'error.main' }}>
            {trend === 'up' ? <TrendingUpIcon sx={{ fontSize: '1rem' }} /> : <TrendingDownIcon sx={{ fontSize: '1rem' }} />}
            <Typography variant="body2">{change}</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── WorkflowCard ─────────────────────────────────────────────────────────────

function WorkflowCard({ label, value, sub, icon, color }) {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%', borderRadius: 2,
        border: `1px solid ${alpha(color || theme.palette.primary.main, 0.18)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">{label}</Typography>
            <Typography variant="h4" fontWeight={700}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ color: color || theme.palette.primary.main, opacity: 0.6 }}>
            {React.cloneElement(icon, { sx: { fontSize: 40 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── RevenueOverview ──────────────────────────────────────────────────────────

function RevenueOverview({ dashboardData, salesStats, periodLabel, comparisonLabel }) {
  const theme = useTheme();
  const thisMonth = parseFloat(dashboardData.revenueThisMonth || 0);
  const lastMonth = parseFloat(dashboardData.revenueLastMonth || 0);
  const change = dashboardData.revenueChange || '0';
  const changeTrend = !change.toString().startsWith('-');

  // Revenue split by type using org counts as proxy (real data)
  const totalOrgs = (dashboardData.schoolCount || 0) + (dashboardData.businessCount || 0);
  const schoolPct = totalOrgs > 0 ? Math.round((dashboardData.schoolCount / totalOrgs) * 100) : 60;
  const bizPct    = totalOrgs > 0 ? Math.round((dashboardData.businessCount / totalOrgs) * 100) : 30;
  const otherPct  = Math.max(0, 100 - schoolPct - bizPct);

  const bars = [
    { label: 'Schools',      pct: schoolPct, color: theme.palette.primary.main },
    { label: 'Businesses',   pct: bizPct,    color: theme.palette.secondary.main },
    { label: 'Other',        pct: otherPct,  color: theme.palette.info.main },
  ];

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%', borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsertChartIcon sx={{ color: 'primary.main' }} /> Revenue Overview
        </Typography>
      </Box>
      <CardContent sx={{ p: 3 }}>
        {/* This period vs last */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">{periodLabel || 'This Month'}</Typography>
            <Typography variant="h5" fontWeight={700}>{formatCurrency(thisMonth)}</Typography>
          </Box>
          <Chip
            size="small"
            label={`${changeTrend ? '+' : ''}${change}%`}
            sx={{
              bgcolor: alpha(changeTrend ? theme.palette.success.main : theme.palette.error.main, 0.1),
              color: changeTrend ? theme.palette.success.main : theme.palette.error.main,
              fontWeight: 600,
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
          vs {formatCurrency(lastMonth)} {comparisonLabel || 'last month'}
        </Typography>

        <Divider sx={{ mb: 2.5 }} />

        {/* Pipeline split */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Pipeline — {periodLabel || 'This Month'}
        </Typography>
        <Box sx={{ display: 'flex', mb: 2, gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">In Progress</Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatCurrency(salesStats.monthlyRevenueInProgress)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Invoiced</Typography>
            <Typography variant="body1" fontWeight={600} sx={{ color: 'success.main' }}>
              {formatCurrency(salesStats.monthlyRevenueInvoiced)}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Org type breakdown */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Organisation Mix
        </Typography>
        {bars.map(({ label, pct, color }) => (
          <Box key={label} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight={500}>{pct}%</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 7, borderRadius: 4,
                bgcolor: alpha(color, 0.12),
                '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
              }}
            />
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── AdminDashboard ───────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { token } = useAuth();
  const theme = useTheme();

  const [period, setPeriod] = useState(getDefaultPeriod);
  const periodLabel    = formatPeriodLabel(period);
  const periodShort    = period.view === 'year' ? 'this year'  : 'this month';
  const comparisonLabel = period.view === 'year' ? 'last year' : 'last month';

  const [dashboardData, setDashboardData] = useState({
    organizationCount: 0, activeOrders: 0, totalRevenue: 0, newEnquiries: 0,
    ordersThisMonth: 0, recentOrders: [], ordersChange: '+0%', revenueChange: '0',
    revenueThisMonth: 0, revenueLastMonth: 0, schoolCount: 0, businessCount: 0,
    enquiriesThisMonth: 0,
  });
  const [salesStats, setSalesStats] = useState({
    convertedEnquiries: 0, activeAdOrders: 0, followUpsDue: 0,
    monthlyRevenueInProgress: 0, monthlyRevenueInvoiced: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const periodParams = period.view === 'year'
        ? `?year=${period.year}&view=year`
        : `?year=${period.year}&month=${period.month}`;

      const [statsRes, salesRes] = await Promise.all([
        axios.get(`${config.apiUrl}/api/dashboard/stats${periodParams}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${config.apiUrl}/api/dashboard/sales-workflow-stats${periodParams}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (statsRes.data?.success) {
        const d = statsRes.data.data;
        setDashboardData({
          organizationCount: d.organizationCount || 0,
          activeOrders: d.activeOrders || 0,
          totalRevenue: parseFloat(d.totalRevenue) || 0,
          newEnquiries: d.newEnquiries || 0,
          ordersThisMonth: d.ordersThisMonth || 0,
          recentOrders: d.recentOrders || [],
          ordersChange: d.ordersChange ? `${d.ordersChange > 0 ? '+' : ''}${d.ordersChange}%` : '+0%',
          revenueChange: d.revenueChange || '0',
          revenueThisMonth: parseFloat(d.revenueThisMonth) || 0,
          revenueLastMonth: parseFloat(d.revenueLastMonth) || 0,
          schoolCount: d.schoolCount || 0,
          businessCount: d.businessCount || 0,
          enquiriesThisMonth: d.enquiriesThisMonth || 0,
        });
      }
      if (salesRes.data?.success) {
        const s = salesRes.data.data;
        setSalesStats({
          convertedEnquiries: s.convertedEnquiries || 0,
          activeAdOrders: s.activeAdOrders || 0,
          followUpsDue: s.followUpsDue || 0,
          monthlyRevenueInProgress: s.monthlyRevenueInProgress || 0,
          monthlyRevenueInvoiced: s.monthlyRevenueInvoiced || 0,
        });
      }
    } catch (e) {
      console.error('Error fetching admin dashboard data:', e);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const revenueChangeTrend = !dashboardData.revenueChange.toString().startsWith('-');
  const ordersChangeTrend  = dashboardData.ordersChange.startsWith('+');

  return (
    <Box>
      {/* ── Header ── */}
      <Box
        sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 4, pb: 2, borderBottom: `1px solid ${theme.palette.divider}`, flexWrap: 'wrap', gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}
          >
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Business overview for {periodLabel}
          </Typography>
        </Box>
        <MonthYearPicker value={period} onChange={setPeriod} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* ── Row 1: Global Metrics ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Revenue"
            value={formatCurrency(dashboardData.revenueThisMonth)}
            change={`${revenueChangeTrend ? '+' : ''}${dashboardData.revenueChange}% vs ${comparisonLabel}`}
            trend={revenueChangeTrend ? 'up' : 'down'}
            color={theme.palette.primary.main}
            icon={<MonetizationOnIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Orders"
            value={dashboardData.ordersThisMonth}
            change={dashboardData.ordersChange}
            trend={ordersChangeTrend ? 'up' : 'down'}
            color={theme.palette.info.main}
            icon={<AssignmentIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Organisations"
            value={dashboardData.organizationCount}
            color={theme.palette.secondary.main}
            icon={<BusinessIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label="Enquiries"
            value={dashboardData.enquiriesThisMonth}
            change={`vs ${comparisonLabel}`}
            trend="up"
            color={theme.palette.warning.main}
            icon={<PeopleIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Row 2: Sales Workflow ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Sales Workflow — {periodLabel}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <WorkflowCard
            label="Converted Enquiries"
            value={salesStats.convertedEnquiries}
            sub={`Ordered ${periodShort}`}
            color={theme.palette.success.main}
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <WorkflowCard
            label="Active Ad Orders"
            value={salesStats.activeAdOrders}
            sub="In progress"
            color={theme.palette.info.main}
            icon={<AssignmentIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <WorkflowCard
            label="Follow-Ups Due"
            value={salesStats.followUpsDue}
            sub="Today or overdue"
            color={salesStats.followUpsDue > 0 ? theme.palette.warning.main : theme.palette.success.main}
            icon={<ScheduleIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <WorkflowCard
            label="In Progress"
            value={`$${(salesStats.monthlyRevenueInProgress / 1000).toFixed(1)}K`}
            sub={`Revenue ${periodShort}`}
            color={theme.palette.info.dark}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <WorkflowCard
            label="Invoiced"
            value={`$${(salesStats.monthlyRevenueInvoiced / 1000).toFixed(1)}K`}
            sub={`Revenue ${periodShort}`}
            color={theme.palette.success.main}
            icon={<CheckCircleIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Row 3: Recent Orders + Revenue Overview ── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Recent Orders */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
            }}
          >
            <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentIcon sx={{ color: 'primary.main' }} /> Recent Orders
              </Typography>
              <Button component={Link} to="/app/orders" size="small" variant="outlined">
                View All
              </Button>
            </Box>
            {dashboardData.recentOrders.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Business / School</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Sales Person</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.recentOrders.slice(0, 8).map(order => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>ORD-{order.id}</Typography>
                        </TableCell>
                        <TableCell>{order.businessName || `Order #${order.id}`}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {formatCurrency(parseFloat(order.totalPrice) || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {order.salesPersonName || 'Unassigned'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateLong(order.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={order.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No orders found.</Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Revenue Overview */}
        <Grid item xs={12} lg={4}>
          <RevenueOverview
            dashboardData={dashboardData}
            salesStats={salesStats}
            periodLabel={periodLabel}
            comparisonLabel={comparisonLabel}
          />
        </Grid>
      </Grid>

      {/* ── Leave ── */}
      <LeaveSection />

      {/* ── Staff Schedule ── */}
      <StaffScheduleSection />
    </Box>
  );
}
